/* globals firebaseConfig */
import store from "@/store";
import {
  getAuth,
  OAuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithEmailAndPassword,
  multiFactor,
  TotpMultiFactorGenerator,
  sendEmailVerification,
  getMultiFactorResolver,
  onAuthStateChanged,
  signOut,
  applyActionCode,
  SAMLAuthProvider,
} from "firebase/auth";
import { initializeApp, getApp, getApps } from "firebase/app";
import { AuthApi } from "@/api";

let refreshTokenIntervalId = null;

initializeApp(firebaseConfig);

getAuth().onAuthStateChanged(async (user) => {
  const authToken = await AuthApi.getAuthToken();
  const isFirebaseProvider = !!store.getters["user/isFirebaseProvider"];

  if (user && authToken && isFirebaseProvider) {
    const userData = {
      token: user.accessToken,
      data: getFormattedUserData(user),
    };

    store.dispatch("auth/loginWithUser", {
      user: userData,
      token: userData.token,
    });
  }
});

async function refreshToken() {
  const auth = getFirebaseAuth();

  if (auth.currentUser) {
    const newToken = await auth.currentUser.getIdToken(true);

    if (newToken) {
      AuthApi.setAuthToken(newToken);
      AuthApi.setAuthorizationHeader(newToken);
    }
  }
}

function startTokenRefreshInterval() {
  if (refreshTokenIntervalId) {
    clearInterval(refreshTokenIntervalId);
  }

  refreshTokenIntervalId = setInterval(async () => {
    try {
      const auth = getFirebaseAuth();

      if (auth.currentUser) {
        // Force token refresh
        const newToken = await auth.currentUser.getIdToken(true);

        if (newToken) {
          return newToken;
        }
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);
    }
  }, 3300 * 1000); // 55 minutes in milliseconds
}

function getFirebaseAuth() {
  const isFirebaseAppInitialized = Boolean(getApps().length);

  return isFirebaseAppInitialized ? getAuth(getApp()) : null;
}

function getFormattedUserData(user) {
  const currentUser = store.getters["user/currentUser"];
  const attributes = currentUser?.attributes || {};

  return {
    id: user.uid,
    attributes: {
      fullName: user.displayName,
      email: user.email,
      ...attributes,
    },
  };
}

function getFirebaseAuthUser() {
  const auth = getFirebaseAuth();

  if (auth.currentUser) {
    return {
      data: getFormattedUserData(auth.currentUser),
      included: [
        {
          type: "token",
          id: auth.currentUser.accessToken,
        },
      ],
    };
  }

  return null;
}

async function signInWithProvider(provider) {
  async function loginUser(user) {
    const token = await user.getIdToken();
    const userData = {
      token,
      data: getFormattedUserData(user),
    };

    store.dispatch("auth/loginWithUser", { user: userData, token });
  }

  async function handleUserWithExistingCredential(email, credential) {
    const [signInMethod] = await fetchSignInMethodsForEmail(firebaseAuth, email);

    if (!signInMethod) {
      throw new Error("Account is linked to an unsupported provider");
    }

    const linkedProvider = getAuthProvider(signInMethod);

    linkedProvider.setCustomParameters({ login_hint: email });

    const { user } = await signInWithPopup(firebaseAuth, linkedProvider);

    await linkWithCredential(user, credential);

    await loginUser(user);
  }

  const firebaseAuth = getFirebaseAuth();

  if (!firebaseAuth) {
    throw new Error("Firebase app has not been initialized");
  }

  const authProvider = getAuthProvider(provider);

  try {
    const { user } = await signInWithPopup(firebaseAuth, authProvider);

    await loginUser(user);
  } catch (error) {
    if (error.code !== "auth/account-exists-with-different-credential") {
      throw error;
    }

    const credential = OAuthProvider.credentialFromError(error);

    await handleUserWithExistingCredential(error.customData.email, credential);
  }
}

function getAuthProvider(providerId) {
  switch (providerId) {
    case GoogleAuthProvider.PROVIDER_ID:
      return new GoogleAuthProvider().setCustomParameters({ prompt: "select_account" });
    case FacebookAuthProvider.PROVIDER_ID:
      return new FacebookAuthProvider();
    default:
      throw new Error(`Unknown provider: ${providerId}`);
  }
}

/**
 * Returns the
 * @param {*=} firebaseUser instance of the firebase user.
 * @param {boolean=} refresh specifies whether or not to force refresh the token.(`false` by default.)
 * @returns {Promise}
 */
function getFirebaseAuthToken(firebaseUser, refresh = false) {
  // Note: `getIdToken` function is synchronous if `true` did't passed
  // if `true` is passed then token refresh will be forced and firebase will send a request for tokens refresh.
  if (firebaseUser) {
    return firebaseUser.getIdToken(refresh);
  }

  const auth = getFirebaseAuth();

  if (auth.currentUser) {
    return auth.currentUser.getIdToken(refresh);
  }

  return Promise.resolve(null);
}

async function createFirebaseUserWithEmailAndPassword({ email, password, fullName }) {
  const firebaseAuth = getFirebaseAuth();

  if (!firebaseAuth) {
    throw new Error("Firebase app has not been initialized");
  }

  const { user } = await createUserWithEmailAndPassword(firebaseAuth, email, password);

  await updateProfile(firebaseAuth.currentUser, { displayName: fullName });

  return {
    included: [
      {
        type: "token",
        id: user.accessToken,
      },
    ],
    data: getFormattedUserData(user),
  };
}

async function signInUserWithEmailAndPassword({ email, password }) {
  const firebaseAuth = getFirebaseAuth();

  if (!firebaseAuth) {
    throw new Error("Firebase app has not been initialized");
  }

  const { user } = await signInWithEmailAndPassword(firebaseAuth, email, password);

  return {
    token: user.accessToken,
    data: getFormattedUserData(user),
  };
}

async function getMultiFactorInfo() {
  const auth = getAuth();

  return new Promise((resolve, reject) => {
    onAuthStateChanged(auth, (user) => {
      if (!user) {
        return reject(new Error("User is not authenticated"));
      }

      resolve(multiFactor(user));
    });
  });
}

async function sendVerificationEmail(currentUser, actionCodeSettings) {
  if (!currentUser) return;

  try {
    await sendEmailVerification(currentUser, actionCodeSettings);
  } catch (error) {
    console.error("Error sending verification email: ", error);
  }
}

async function generateTotpSecret(currentUser) {
  const actionCodeSettings = {
    url: `${window.location.origin}/email-verification`,
  };

  if (!currentUser.emailVerified) {
    await sendVerificationEmail(currentUser, actionCodeSettings);
  }

  const multiFactorSession = await multiFactor(currentUser).getSession();

  try {
    return await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
  } catch (error) {
    return Promise.reject(error);
  }
}

async function verifyTotpCode(totpSecret, verificationCode, currentUser) {
  const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
    totpSecret,
    verificationCode,
  );

  return await multiFactor(currentUser).enroll(multiFactorAssertion, "totp");
}

async function unEnrollTotp(currentUser, mfaEnrollmentId) {
  return await multiFactor(currentUser).unenroll(mfaEnrollmentId);
}

async function signInWithTotp(error, otpFromAuthenticator) {
  const mfaResolver = getMultiFactorResolver(getAuth(), error);
  const enrolledFactors = mfaResolver.hints;

  const totpFactor = enrolledFactors.find(
    (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );

  if (totpFactor) {
    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
      totpFactor.uid,
      otpFromAuthenticator,
    );

    const userCredential = await mfaResolver.resolveSignIn(multiFactorAssertion);

    return userCredential;
  }
}

async function handleApplyActionCode(actionCode) {
  return applyActionCode(getAuth(), actionCode);
}

async function signInWithSaml() {
  return signInWithPopup(getAuth(), new SAMLAuthProvider("saml.azuread"));
}

function buildErrorMessage(code) {
  switch (code) {
    case "auth/invalid-verification-code":
      return "Incorrect code. Please try again.";

    case "auth/quota-exceeded":
      return "The maximum number of sign-in attempts has been exceeded. Please try again later.";

    case "auth/totp-challenge-timeout":
      return "Session timed out. Please refresh the page and try again.";

    case "auth/request-contains-an-invalid-argument":
      return "The request contains an invalid argument.";

    case "auth/too-many-enrollment-attempts":
      return "The maximum number of enrollment attempts has been exceeded. Please try again later.";

    default:
      return "An error occurred. Please try again.";
  }
}

const firebaseAuth = {
  getToken: getFirebaseAuthToken,
  getUser: getFirebaseAuthUser,
  getAuth: getFirebaseAuth,
  createUserWithEmailAndPassword: createFirebaseUserWithEmailAndPassword,
  signInWithProvider,
  signInUserWithEmailAndPassword,
  signInWithSaml,
  handleApplyActionCode,
  startTokenRefreshInterval,
  getMultiFactorInfo,
  generateTotpSecret,
  verifyTotpCode,
  unEnrollTotp,
  signInWithTotp,
  signOut,
  buildErrorMessage,
  refreshToken,
};

export default firebaseAuth;
